import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    ArrowUpRight,
    ArrowLeft,
    AlertTriangle,
    Target,
    CheckCircle2,
    Sparkles,
    UserCheck,
    ServerCog,
    ShieldCheck,
    Cloud,
    Compass,
    Workflow,
    LifeBuoy,
} from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Overline } from "@/components/Reveal";
import { MountReveal, CtaLink } from "@/components/pageKit";
import { useSeo } from "@/lib/useSeo";
import { getServiceBySlug, getNavigation } from "@/lib/sanityQueries";
import { peek } from "@/lib/sanity";

const ICONS_BY_SLUG = {
    "managed-it-operational-excellence": ServerCog,
    "cybersecurity-risk-protection": ShieldCheck,
    "cloud-infrastructure-scalability": Cloud,
    "it-strategy-advisory-vcio": Compass,
    "digital-experience-custom-solutions": Workflow,
    "business-continuity-resilience": LifeBuoy,
};

const NotFound = () => (
    <section
        data-testid="service-not-found"
        className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-5 py-40 text-center sm:px-8"
    >
        <span className="grid h-14 w-14 place-items-center rounded-2xl border border-brand-line bg-brand-surface text-brand-accent">
            <AlertTriangle className="h-6 w-6" />
        </span>
        <h1 className="mt-7 font-heading text-4xl font-extrabold tracking-tight text-brand-text sm:text-5xl">
            Service not found
        </h1>
        <p className="mt-5 max-w-md text-base leading-relaxed text-brand-muted">
            The service you're looking for doesn't exist or may have moved. Explore our full range
            of technology services instead.
        </p>
        <Link
            to="/services"
            data-testid="service-not-found-back"
            className="group mt-9 inline-flex items-center gap-2 rounded-control bg-brand-accent px-7 py-3.5 text-sm font-semibold text-brand-on-accent transition-colors duration-150"
        >
            <ArrowLeft className="h-4 w-4 transition-colors duration-150" />
            View all services
        </Link>
    </section>
);

const InfoBlock = ({ icon: Icon, overline, title, children, testid }) => (
    <MountReveal className="rounded-3xl border border-brand-line bg-brand-surface p-8 sm:p-10">
        <div data-testid={testid}>
            <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-xl border border-brand-line bg-brand-bg text-brand-accent">
                    <Icon className="h-5 w-5" />
                </span>
                <Overline>{overline}</Overline>
            </div>
            <h2 className="mt-5 font-heading text-2xl font-bold tracking-tight text-brand-text sm:text-3xl">
                {title}
            </h2>
            <div className="mt-4 text-base leading-relaxed text-brand-muted">{children}</div>
        </div>
    </MountReveal>
);

const ServiceDetailContent = () => {
    const { slug } = useParams();
    const [service, setService] = useState(() => peek(getServiceBySlug, slug));
    const [status, setStatus] = useState(() => (service ? "ready" : "loading")); // loading | ready | notfound
    const [navCta, setNavCta] = useState(() => peek(getNavigation)?.primaryCta ?? null);

    useEffect(() => {
        let active = true;
        const snap = peek(getServiceBySlug, slug);
        if (snap) {
            setService(snap);
            setStatus("ready");
        } else {
            setStatus("loading");
        }
        getServiceBySlug(slug)
            .then((res) => {
                if (!active) return;
                if (res) {
                    setService(res);
                    setStatus("ready");
                } else {
                    setStatus("notfound");
                }
            })
            .catch(() => {
                if (active) setStatus("notfound");
            });
        getNavigation()
            .then((res) => {
                if (active && res?.primaryCta) setNavCta(res.primaryCta);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [slug]);

    // SEO from the resolved service (undefined until ready → no stale metadata).
    useSeo(service?.seo);

    if (status === "loading") {
        return (
            <section className="flex min-h-[60vh] items-center justify-center px-5 py-40">
                <span
                    data-testid="service-loading"
                    className="h-10 w-10 animate-spin rounded-full border-2 border-brand-line border-t-brand-accent"
                    aria-label="Loading service"
                />
            </section>
        );
    }

    if (status === "notfound") return <NotFound />;

    const s = service;
    const Icon = ICONS_BY_SLUG[s.slug] || Sparkles;
    const bookingUrl = navCta?.url || "#";
    const bookingLabel = navCta?.label || "Schedule a Conversation";

    return (
        <>
            {/* HERO / POSITIONING */}
            <section
                data-testid="service-detail-hero"
                className="relative overflow-hidden pt-36 pb-16 sm:pt-44 sm:pb-20"
            >
                <div className="pointer-events-none absolute inset-0 el-grid-bg" aria-hidden />
                <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
                    <MountReveal>
                        <Link
                            to="/services"
                            data-testid="service-detail-back"
                            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-muted transition-colors duration-150 hover:text-brand-accent"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" />
                            All services
                        </Link>
                    </MountReveal>
                    <MountReveal delay={0.05}>
                        <span className="mt-6 inline-flex items-center">
                            <Overline>Service</Overline>
                        </span>
                    </MountReveal>
                    <MountReveal delay={0.1}>
                        <h1
                            data-testid="service-detail-heading"
                            className="mt-6 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-6xl"
                        >
                            {s.name}
                        </h1>
                    </MountReveal>
                    <MountReveal delay={0.15}>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-muted sm:text-lg">
                            {s.shortDescription}
                        </p>
                    </MountReveal>
                    <MountReveal delay={0.2}>
                        <div className="mt-9">
                            <CtaLink
                                href={bookingUrl}
                                label={bookingLabel}
                                openInNewTab
                                variant="primary"
                                testid="service-detail-hero-cta"
                            />
                        </div>
                    </MountReveal>
                </div>
            </section>

            {/* PROBLEM + OUTCOME */}
            <section className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    {s.businessProblem && (
                        <InfoBlock
                            icon={AlertTriangle}
                            overline="The Business Problem"
                            title="What's getting in the way"
                            testid="service-business-problem"
                        >
                            {s.businessProblem}
                        </InfoBlock>
                    )}
                    {s.businessOutcome && (
                        <InfoBlock
                            icon={Target}
                            overline="The Business Outcome"
                            title="What you can expect"
                            testid="service-business-outcome"
                        >
                            {s.businessOutcome}
                        </InfoBlock>
                    )}
                </div>
            </section>

            {/* CAPABILITIES */}
            {s.capabilities?.length > 0 && (
                <section
                    data-testid="service-capabilities"
                    className="border-y border-brand-line bg-brand-surface py-20 sm:py-24"
                >
                    <div className="mx-auto max-w-7xl px-5 sm:px-8">
                        <MountReveal>
                            <Overline>Included Capabilities</Overline>
                        </MountReveal>
                        <MountReveal delay={0.05}>
                            <h2 className="mt-5 max-w-2xl font-heading text-3xl font-extrabold leading-[1.05] tracking-tight text-brand-text sm:text-4xl">
                                What's included
                            </h2>
                        </MountReveal>
                        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {s.capabilities.map((c, i) => (
                                <MountReveal key={i} delay={(i % 3) * 0.05}>
                                    <div className="flex h-full items-start gap-3 rounded-2xl border border-brand-line bg-brand-bg p-5">
                                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-accent" />
                                        <p className="text-sm font-medium leading-snug text-brand-text">
                                            {c}
                                        </p>
                                    </div>
                                </MountReveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* BENEFITS */}
            {s.benefits?.length > 0 && (
                <section
                    data-testid="service-benefits"
                    className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24"
                >
                    <MountReveal>
                        <Overline>Business Benefits</Overline>
                    </MountReveal>
                    <MountReveal delay={0.05}>
                        <h2 className="mt-5 max-w-2xl font-heading text-3xl font-extrabold leading-[1.05] tracking-tight text-brand-text sm:text-4xl">
                            Why it matters for your business
                        </h2>
                    </MountReveal>
                    <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
                        {s.benefits.map((b, i) => (
                            <MountReveal key={i} delay={(i % 2) * 0.05}>
                                <div className="flex h-full items-center gap-4 rounded-2xl border border-brand-line bg-brand-surface p-6">
                                    <span className="font-heading text-2xl font-extrabold tracking-tight text-brand-accent">
                                        {`0${i + 1}`}
                                    </span>
                                    <p className="text-base font-medium leading-snug text-brand-text">
                                        {b}
                                    </p>
                                </div>
                            </MountReveal>
                        ))}
                    </div>
                </section>
            )}

            {/* IDEAL CLIENT */}
            {s.idealClient && (
                <section
                    data-testid="service-ideal-client"
                    className="border-y border-brand-line bg-brand-surface py-20 sm:py-24"
                >
                    <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
                        <MountReveal>
                            <span className="grid h-12 w-12 mx-auto place-items-center rounded-xl border border-brand-line bg-brand-bg text-brand-accent">
                                <UserCheck className="h-5 w-5" />
                            </span>
                        </MountReveal>
                        <MountReveal delay={0.05}>
                            <Overline className="mt-6 flex w-fit">Who it's for</Overline>
                        </MountReveal>
                        <MountReveal delay={0.1}>
                            <p className="mt-5 font-heading text-2xl font-bold leading-snug tracking-tight text-brand-text sm:text-3xl">
                                {s.idealClient}
                            </p>
                        </MountReveal>
                    </div>
                </section>
            )}

            {/* CONSULTATION CTA */}
            <section
                data-testid="service-detail-closing-cta"
                className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28"
            >
                <div className="relative overflow-hidden rounded-card border border-brand-line bg-brand-surface px-6 py-20 text-center sm:px-16 sm:py-24">
                    <div className="pointer-events-none absolute inset-0 el-grid-bg opacity-40" aria-hidden />
                    <MountReveal>
                        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-accent">
                            Start the conversation
                        </span>
                    </MountReveal>
                    <MountReveal delay={0.05}>
                        <h2 className="mx-auto mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.0] tracking-tight text-brand-text sm:text-5xl">
                            {`Ready to strengthen ${s.name}?`}
                        </h2>
                    </MountReveal>
                    <MountReveal delay={0.1}>
                        <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-brand-muted">
                            Let's talk about your current environment and what a stronger technology
                            foundation could look like for your organization.
                        </p>
                    </MountReveal>
                    <MountReveal delay={0.15}>
                        <div className="mt-10 flex justify-center">
                            <CtaLink
                                href={bookingUrl}
                                label={bookingLabel}
                                openInNewTab
                                variant="primary"
                                testid="service-detail-closing-cta-button"
                            />
                        </div>
                    </MountReveal>
                </div>
            </section>
        </>
    );
};

export default function ServiceDetailPage() {
    return (
        <PageLayout>
            <ServiceDetailContent />
        </PageLayout>
    );
}
